/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-no-undef */
import 'dayjs/locale/lt';
import { Alert, Box, Typography } from '@mui/material';

import HeaderTags from '../components/common/HeaderTags';
import XTable from '../components/common/XTable';
import { HistoryDialogProvider } from '../contexts/useHistoryDialog';
import { IVAZDialogProvider } from '../contexts/useIVAZDialog';
import { PageProvider } from '../contexts/usePageContext';
import { ReportDialogProvider } from '../contexts/useReportDialog';
import useTablePage from '../hooks/useTablePage';
import { getTableStart } from '../redux/results/resultsSlice';

type LicenseSummaryProps = {
 totalLicenses: number;
 activeLicenses: number;
 inactiveLicenses: number;
};

const LicenseSummary = ({
 totalLicenses,
 activeLicenses,
 inactiveLicenses,
}: LicenseSummaryProps) => {
 const usedPercentage =
  totalLicenses === 0 ? 0 : Math.round((activeLicenses / totalLicenses) * 100);

 return (
  <Box
   sx={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
    marginBottom: '16px',
    gap: '32px',
   }}
  >
   <Box
    sx={{
     display: 'flex',
     alignItems: 'center',
     gap: '20px',
     flexWrap: 'wrap',
    }}
   >
    <Box
     sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '16px',
      paddingRight: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      borderRadius: '24px',
      backgroundColor: '#f3f5fd',
      border: '1px solid #6080ff',
      width: '200px',
      minWidth: '200px',
     }}
    >
     <Typography
      variant="body2"
      sx={{
       fontSize: '12px',
       color: '#000000',
       whiteSpace: 'nowrap',
      }}
     >
      Licencijos
     </Typography>
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       width: '32px',
       height: '32px',
       borderRadius: '21px',
       border: '1px solid #dddddd',
      }}
      aria-label="Iš viso licencijų"
      role="status"
     >
      <Typography
       sx={{
        fontSize: '16px',
        fontWeight: 500,
        color: '#2b2b2b',
        textAlign: 'center',
       }}
      >
       {totalLicenses}
      </Typography>
     </Box>
    </Box>
    <Box
     sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '16px',
      paddingRight: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      borderRadius: '24px',
      backgroundColor: '#e7fff0',
      width: '200px',
      minWidth: '200px',
     }}
    >
     <Typography
      variant="body2"
      sx={{
       fontSize: '12px',
       color: '#000000',
       whiteSpace: 'nowrap',
      }}
     >
      Aktyvios
     </Typography>
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       width: '32px',
       height: '32px',
       borderRadius: '21px',
       border: '1px solid #01c0ae',
      }}
      aria-label="Aktyvios licencijos"
      role="status"
     >
      <Typography
       sx={{
        fontSize: '16px',
        fontWeight: 500,
        color: '#01c0ae',
        textAlign: 'center',
       }}
      >
       {activeLicenses}
      </Typography>
     </Box>
    </Box>
    <Box
     sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '16px',
      paddingRight: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      borderRadius: '24px',
      backgroundColor: '#ffdada',
      width: '200px',
      minWidth: '200px',
     }}
    >
     <Typography
      variant="body2"
      sx={{
       fontSize: '12px',
       color: '#000000',
       whiteSpace: 'nowrap',
      }}
     >
      Neaktyvios
     </Typography>
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
       width: '32px',
       height: '32px',
       borderRadius: '21px',
       border: '1px solid #fe5f5f',
      }}
      aria-label="Neaktyvios licencijos"
      role="status"
     >
      <Typography
       sx={{
        fontSize: '16px',
        fontWeight: 500,
        color: '#fe5f5f',
        textAlign: 'center',
       }}
      >
       {inactiveLicenses}
      </Typography>
     </Box>
    </Box>
   </Box>
   <Box
    sx={{
     display: 'flex',
     flexDirection: 'column',
     gap: '12px',
     width: '300px',
     minWidth: '260px',
    }}
   >
    <Box
     sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      width: '100%',
     }}
    >
     <Typography
      sx={{
       fontSize: '14px',
       color: '#2b2b2b',
       whiteSpace: 'nowrap',
      }}
     >
      Išnaudotos licencijos
     </Typography>
     <Box
      sx={{
       display: 'flex',
       alignItems: 'center',
       gap: '12px',
      }}
     >
      <Typography
       sx={{
        fontSize: '14px',
        color: '#000000',
        whiteSpace: 'nowrap',
       }}
      >
       {activeLicenses} iš {totalLicenses}
      </Typography>
      <Typography
       sx={{
        fontSize: '14px',
        color: '#969696',
        whiteSpace: 'nowrap',
       }}
      >
       {usedPercentage}%
      </Typography>
     </Box>
    </Box>
    <Box
     sx={{
      position: 'relative',
      width: '100%',
      height: '8px',
      borderRadius: '4px',
      border: '1px solid #6080ff',
      overflow: 'hidden',
     }}
     role="progressbar"
     aria-valuenow={usedPercentage}
     aria-valuemin={0}
     aria-valuemax={100}
     aria-label="Išnaudotų licencijų procentas"
    >
     <Box
      sx={{
       position: 'absolute',
       top: 0,
       left: 0,
       height: '100%',
       width: `${usedPercentage}%`,
       backgroundColor: '#6080ff',
      }}
     />
    </Box>
   </Box>
  </Box>
 );
};

const NAME = 'settings';
const NAME_UPPER = NAME.charAt(0).toUpperCase() + NAME.slice(1);

const AdminLicenses = () => {
 const { apiRef, items, rowCount, loading, dispatch } = useTablePage(NAME);

 const totalLicenses = 4;
 const activeLicenses = 3;
 const inactiveLicenses = 1;

 return (
  <PageProvider pageName={NAME}>
   <ReportDialogProvider>
    <HistoryDialogProvider>
     <IVAZDialogProvider>
      <HeaderTags title={NAME_UPPER} description={NAME_UPPER} />
      <Alert severity="error" onClose={() => {}} sx={{ mb: 4 }}>
       Liko 5 d. iki „Baltpool“ užbaigimo
      </Alert>
      <LicenseSummary
       totalLicenses={totalLicenses}
       activeLicenses={activeLicenses}
       inactiveLicenses={inactiveLicenses}
      />

      <XTable
       columns={[]}
       apiRef={apiRef}
       table={NAME}
       rows={items}
       rowCount={parseInt(rowCount, 10)}
       fetchData={(data: Record<string, unknown>) =>
        dispatch(getTableStart({ table: NAME, ...data }))
       }
       isServerMode
       loading={loading.getTable || false}
      />
     </IVAZDialogProvider>
    </HistoryDialogProvider>
   </ReportDialogProvider>
  </PageProvider>
 );
};

export default AdminLicenses;

